import { OstState, ProjectModel, OstNode, InsightNode, InsightLink, TriageItem } from '../types';
import { normalizeOstState, getDefaultNodePanelPosition, createEmptyOst } from './ostUtils';

export interface ParsedZey {
    project: { id?: string; name?: string };
    ost: OstState;
    /** Insights are parsed back into the ost.insights field on import. */
    insights?: InsightNode[];
    /** Insight links are parsed back into ost.insightLinks on import. */
    insightLinks?: InsightLink[];
    /** Triage items are parsed back into the ost.triageItems field on import. */
    triageItems?: TriageItem[];
}

// Supports section names with uppercase letters and underscores (e.g. [INSIGHT_LINKS])
const SECTION_RE = /\[([A-Z_]+)\]\s*\n([\s\S]*?)(?=\n\[[A-Z_]+\]|$)/g;

export function formatProjectToZey(project: ProjectModel): string {
    // `project.ost` wins when present: callers that have a specific tree in hand set it
    // explicitly (AppShell's export spreads the active project — `osts` array included — and
    // overwrites `ost` with the tree currently open in the store). Preferring `osts[0]` here
    // would silently export a different, staler tree than the one the user is looking at
    // whenever the project has more than one. `osts[0]` remains the fallback for a project
    // model straight from the API, and the empty tree the fallback for a brand-new project.
    const ost = project.ost
        || (project.osts && project.osts.length > 0 ? project.osts[0].ost : undefined)
        || createEmptyOst({ nodePanelPosition: { x: 20, y: 120 } });

    const nodes: OstNode[] = [];
    if (ost.outcome) nodes.push(ost.outcome);
    nodes.push(...(ost.opportunities || []));
    nodes.push(...(ost.solutions || []));
    nodes.push(...(ost.userStories || []));

    const ostSection = {
        project: { id: project._id, name: project.name },
        nodes,
        positions: ost.positions || {},
        nodePanelPosition: ost.nodePanelPosition || getDefaultNodePanelPosition(),
        solutionChildType: ost.solutionChildType || 'story',
        connections: ost.connections || [],
    };

    // Serialize insights as a separate section for readability and forward compatibility
    const insightsSection = ost.insights || [];

    // Serialize insight links (text-to-opportunity connections)
    const insightLinksSection = ost.insightLinks || [];

    // Serialize triage items (right-panel backlog items)
    const triageItemsSection = ost.triageItems || [];

    const parts = [
        '# ZEYTUNE EXPORT',
        '',
        '[OST]',
        JSON.stringify(ostSection, null, 2),
        '',
        '[INSIGHTS]',
        JSON.stringify(insightsSection, null, 2),
        '',
        '[INSIGHT_LINKS]',
        JSON.stringify(insightLinksSection, null, 2),
        '',
        '[TRIAGE_ITEMS]',
        JSON.stringify(triageItemsSection, null, 2),
    ];
    return parts.join('\n');
}

/**
 * A `.zey` file is user-supplied and hand-editable, so nothing inside it can be
 * trusted to have the shape its field name implies. These guards sanitise the
 * container: a malformed value is DISCARDED in favour of a safe default rather
 * than rejecting the whole import, so one bad field cannot cost a user their
 * entire tree.
 *
 * Node contents stay deliberately permissive — unknown fields are carried
 * through, because the format has to round-trip trees written by newer builds.
 */
const VALID_NODE_TYPES = new Set(['outcome', 'opportunity', 'solution', 'story', 'experiment', 'enablerNode']);

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPoint(value: unknown): value is { x: number; y: number } {
    return isPlainObject(value) && Number.isFinite(value.x) && Number.isFinite(value.y);
}

/** Keeps only the entries that are genuine finite {x, y} points. */
function sanitizePositions(raw: unknown): Record<string, { x: number; y: number }> {
    if (!isPlainObject(raw)) return {};
    const out: Record<string, { x: number; y: number }> = {};
    for (const [id, pos] of Object.entries(raw)) {
        if (isPoint(pos)) out[id] = { x: pos.x, y: pos.y };
    }
    return out;
}

export function parseZey(text: string): ParsedZey {
    if (typeof text !== 'string' || !text.trim()) {
        throw new Error('Empty or invalid ZEY file');
    }

    const sections: Record<string, string> = {};
    let match: RegExpExecArray | null;
    while ((match = SECTION_RE.exec(text))) {
        const sectionName = match[1];
        const sectionBody = match[2].trim();
        sections[sectionName] = sectionBody;
    }

    if (!sections.OST) {
        throw new Error('Missing [OST] section in ZEY file');
    }

    let ostPayload: Record<string, unknown>;
    try {
        const raw: unknown = JSON.parse(sections.OST);
        if (!isPlainObject(raw)) throw new Error('not an object');
        ostPayload = raw;
    } catch (e) {
        throw new Error('Invalid JSON in [OST] section');
    }

    // Parse the [INSIGHTS] section — an array of InsightNode objects
    let parsedInsights: InsightNode[] = [];
    if (sections.INSIGHTS) {
        try {
            const raw = JSON.parse(sections.INSIGHTS);
            // Support both legacy object `{}` format and the new array format
            parsedInsights = Array.isArray(raw) ? (raw as InsightNode[]) : [];
        } catch {
            parsedInsights = [];
        }
    }

    // Parse the [INSIGHT_LINKS] section — an array of InsightLink objects
    let parsedInsightLinks: InsightLink[] = [];
    if (sections.INSIGHT_LINKS) {
        try {
            const raw = JSON.parse(sections.INSIGHT_LINKS);
            parsedInsightLinks = Array.isArray(raw) ? (raw as InsightLink[]) : [];
        } catch {
            parsedInsightLinks = [];
        }
    }

    // Parse the [TRIAGE_ITEMS] section — an array of TriageItem objects
    let parsedTriageItems: TriageItem[] = [];
    if (sections.TRIAGE_ITEMS) {
        try {
            const raw = JSON.parse(sections.TRIAGE_ITEMS);
            parsedTriageItems = Array.isArray(raw) ? (raw as TriageItem[]) : [];
        } catch {
            parsedTriageItems = [];
        }
    }

    const nodeMap = new Map<string, unknown>();
    const output: OstState = {
        outcome: null,
        opportunities: [],
        solutions: [],
        userStories: [],
        positions: sanitizePositions(ostPayload.positions),
        nodePanelPosition: isPoint(ostPayload.nodePanelPosition)
            ? ostPayload.nodePanelPosition
            : getDefaultNodePanelPosition(),
        solutionChildType: ostPayload.solutionChildType === 'experiment' ? 'experiment' : 'story',
    };

    // A node needs a string id and a type this build understands; anything else is
    // dropped rather than being written into the store.
    const rawNodes: unknown[] = Array.isArray(ostPayload.nodes) ? ostPayload.nodes : [];
    const validNodes = rawNodes.filter(
        (node): node is Record<string, unknown> =>
            isPlainObject(node)
            && typeof node.id === 'string'
            && node.id.length > 0
            && typeof node.type === 'string'
            && VALID_NODE_TYPES.has(node.type),
    );

    validNodes.forEach((node) => {
        nodeMap.set(node.id as string, node);
    });

    validNodes.forEach((node: any) => {
        const type = node.type;
        if (type === 'outcome') {
            output.outcome = node;
        } else if (type === 'opportunity') {
            output.opportunities.push(node);
        } else if (type === 'solution') {
            output.solutions.push(node);
        } else if (type === 'story' || type === 'experiment' || type === 'enablerNode') {
            output.userStories.push(node);
        }
    });

    const rawOst: OstState = {
        ...output,
        connections: Array.isArray(ostPayload.connections) ? ostPayload.connections : [],
        insights: parsedInsights,
        insightLinks: parsedInsightLinks,
        triageItems: parsedTriageItems,
    };

    const normalized = normalizeOstState(rawOst);

    const parsed: ParsedZey = {
        project: isPlainObject(ostPayload.project) ? (ostPayload.project as ParsedZey['project']) : { id: '', name: '' },
        ost: normalized,
        insights: parsedInsights,
        insightLinks: parsedInsightLinks,
        triageItems: parsedTriageItems,
    };

    return parsed;
}
